<script>
import {
  computed,
  ref,
  nextTick,
} from 'vue';

import { useI18n } from 'vue-i18n';
import {
  LpSelect,
  LpDropdown,
  LpCard,
  LpMarkerBtn,
  LpModal,
  LpButton,
  LpDivider,
  LpInlineNotifications,
  useInlineNotifications,
} from '@thehotelsnetwork/lampo';
import { ArrowDownCircleIcon } from '@thehotelsnetwork/icons/vue/linear';

import useSectionState from '@/state/useSectionState';
import useUserState from '@/state/useUserState';
import handleDownloadReport from '@/utils/handleDownloadReport';

// Components
import MetricDescriptionBox from '@/components/MetricDescriptionBox/MetricDescriptionBox.vue';

export default {
  components: {
    ArrowDownCircleIcon,
    MetricDescriptionBox,
    LpSelect,
    LpDropdown,
    LpCard,
    LpMarkerBtn,
    LpModal,
    LpButton,
    LpDivider,
    LpInlineNotifications,
  },
  props: [
    'type',
    'title',
    'id',
    'tpConfig',
    'selectedMetric',
    'metrics',
    'selectedInterval',
    'intervals',
    'compareFirstCompset',
    'compareSecondCompset',
    'defaultSort',
    'changeMetric',
    'changeInterval',
    'changeCompareSecondCompset',
    'changeCompareFirstCompset',
    'changeDefaultSort',
    'loading',
  ],
  setup(props) {
    const {
      hotel,
      compsets,
      compsetsFilter,
      getCompsetData,
    } = useSectionState();

    const { t } = useI18n();

    const { userData } = useUserState();

    const selectedIntervalLabel = ref(props.selectedInterval);
    const compareFirstCompsetLabel = ref(props.compareFirstCompset);
    const compareSecondCompsetLabel = ref(props.compareSecondCompset);
    const isBreakdownTable = computed(() => !!props.id.match(/breakdown/));
    const inlineNotifications = ref(null);
    const emailPromptRef = ref(null);

    const {
      showWarning,
      cleanNotifications,
    } = useInlineNotifications(inlineNotifications);

    const showEmailPrompt = ref(false);
    const emailSubmitted = ref(false);
    const email = ref(null);

    const intervalOptions = computed(
      () => props.intervals.map(
        (interval) => ({ value: interval.name, label: interval.placeholder }),
      ),
    );

    const compareFirstCompsetOptions = computed(() => {
      if (compsetsFilter.value.filter((c) => c !== props.compareFirstCompset).length < 2
      || !props.compareFirstCompset) {
        return null;
      }
      return compsets.value
        .filter(
          (c) => c.compsetTag !== compareSecondCompsetLabel.value
            && compsetsFilter.value.indexOf(c.compsetTag) !== -1,
        )
        .map((i) => ({ label: i.compset, value: i.compsetTag }));
    });

    const compareSecondCompsetOptions = computed(() => {
      if (compsetsFilter.value.filter((c) => c !== props.compareSecondCompset).length < 2
      || !props.compareSecondCompset) {
        return null;
      }
      return compsets.value
        .filter(
          (c) => c.compsetTag !== compareFirstCompsetLabel.value
            && compsetsFilter.value.indexOf(c.compsetTag) !== -1,
        )
        .map((i) => ({ label: i.compset, value: i.compsetTag }));
    });

    const compareFirstCompsetName = computed(
      () => (getCompsetData(props.compareFirstCompset)).compset,
    );
    const compareSecondCompsetName = computed(
      () => (getCompsetData(props.compareSecondCompset)).compset,
    );

    const downloadOptions = computed(() => {
      const tmpOptions = [];

      if (props.type === 'table') {
        tmpOptions.push({ label: 'CSV', value: 'csv' });
      } else if (props.type === 'chart') {
        tmpOptions.push({ label: 'PDF', value: 'pdf' });
      }

      return tmpOptions;
    });

    const showRecipientModal = async () => {
      email.value = userData.email;
      showEmailPrompt.value = true;

      await nextTick();

      showWarning({
        text: t('Depending on the amount of properties in your account, your CSV file might take up to 15 minutes to arrive in your inbox.'),
        static: true,
        hideIcon: true,
      });
    };

    const handleEmailPromptSubmit = (format) => {
      cleanNotifications();

      showEmailPrompt.value = false;

      handleDownloadReport({
        format,
        id: props.id,
        recipient: email.value,
      });
    };

    return {
      isBreakdownTable,
      emailPromptRef,
      inlineNotifications,
      selectedIntervalLabel,
      intervalOptions,
      compsetsFilter,
      hotel,
      compareFirstCompsetName,
      compareSecondCompsetName,
      downloadOptions,
      handleEmailPromptSubmit,
      handleDownloadReport,
      showRecipientModal,
      showEmailPrompt,
      email,
      emailSubmitted,
      compareFirstCompsetOptions,
      compareSecondCompsetOptions,
      compareFirstCompsetLabel,
      compareSecondCompsetLabel,
    };
  },
};

</script>

<template>
  <div>
    <LpModal
      ref="emailPromptRef"
      v-model="showEmailPrompt"
      content-classes="max-w-3xl"
      data-testid="email-prompt-modal"
    >
      <template #content>
        <div class="text-3xl font-bold font-display">
          {{ $t('Download CSV') }}
        </div>

        <div class="mt-2 text-base font-medium">
          {{ $t('Email address - we will send your CSV here') }}
        </div>

        <LpInput
          v-model="email"
          class="mt-2"
        />

        <LpInlineNotifications
          ref="inlineNotifications"
          class="mt-4"
        />

        <LpDivider class="mt-6" />

        <div class="flex justify-end mt-6">
          <LpButton
            data-testid="email-prompt-submit"
            @click="handleEmailPromptSubmit('csv')"
          >
            {{ $t('Send CSV') }}
          </LpButton>
        </div>
      </template>
    </LpModal>

    <div class="flex justify-between controls-header">
      <MetricDescriptionBox
        :metric="title"
        :tp-config="tpConfig"
      />

      <div class="flex gap-3 items-center justify-between transition-all duration-500 ease-in-out opacity-0 filters">
        <template v-if="selectedInterval">
          <LpSelect
            v-model="selectedIntervalLabel"
            :options="intervalOptions"
            y-position="bottom"
            size="small"
            x-position="right"
            @update:model-value="changeInterval"
          />
        </template>

        <div
          v-if="compsetsFilter.length > 1 && compareFirstCompsetOptions && compareSecondCompsetOptions"
          class="flex items-center gap-3"
        >
          <div>
            {{ $t('Compare') }}
          </div>
          <LpSelect
            v-model="compareFirstCompsetLabel"
            class="rank-compset-selector"
            :options="compareFirstCompsetOptions"
            y-position="bottom"
            menu-width="14rem"
            size="small"
            x-position="right"
            @update:model-value="changeCompareFirstCompset"
          />
          <div>
            {{ $t('to') }}
          </div>
          <LpSelect
            v-model="compareSecondCompsetLabel"
            class="rank-compset-selector"
            :options="compareSecondCompsetOptions"
            y-position="bottom"
            menu-width="14rem"
            size="small"
            x-position="right"
            @update:model-value="changeCompareSecondCompset"
          />
        </div>

        <LpDropdown
          v-if="!loading && downloadOptions.length"
          x-position="right"
        >
          <template #btn>
            <button
              type="button"
              class="block"
              data-testid="download-options-trigger"
            >
              <ArrowDownCircleIcon class="w-6 h-6" />
            </button>
          </template>
          <template #menu="{ closeMenu }">
            <LpCard
              rounded-size="big"
              class="flex flex-col w-32 gap-2 p-2 text-sm"
            >
              <LpMarkerBtn
                v-for="option in downloadOptions"
                :key="option.value"
                data-testid="download-option"
                @click="
                  () => isBreakdownTable
                    ? showRecipientModal()
                    : handleDownloadReport({ id, format: option.value })
                "
                @click.prevent="closeMenu"
              >
                {{ option.label }}
              </LpMarkerBtn>
            </LpCard>
          </template>
        </LpDropdown>
      </div>
    </div>

    <div
      v-if="compareSecondCompsetName"
      class="relative mx-auto mt-10 text-center"
      :style="{ width: '200px' }"
    >
      <span
        class="transition duration-200 ease-in-out hover:cursor-pointer hover:opacity-50"
        @click="changeDefaultSort"
      >
        ⇄
      </span>
      <div
        :style="{ transform: 'translateX(-100%)', whiteSpace: 'nowrap' }"
        class="absolute left-0 inline-block"
      >
        {{ compareFirstCompsetName }}
      </div>
      <div
        :style="{ transform: 'translateX(100%)', whiteSpace: 'nowrap' }"
        class="absolute right-0 inline-block"
      >
        {{ compareSecondCompsetName }}
      </div>
    </div>
  </div>
</template>
